/**
 * ZeroMonth — the 0 as a month. Face 2 of the signature object.
 *
 * Face 1 (Zero.tsx) is a day you step through hour by hour. This is the same
 * mark one scale up: the full circle is ONE Prest0n's monthly allocation, and
 * the five arcs are what a real month of work spends it on. The arithmetic —
 * and the reason the circle genuinely IS the month rather than being drawn to
 * look like it — is in zeroMonth.ts.
 *
 * IT IS A DISPLAY YOU CAN READ, NOT A SECOND CALCULATOR. Select an arc and it
 * tells you what that slice is, how many, at what published rate, and what
 * share of the month it takes. That is deliberately the SAME contract as face
 * 1: select and read. The reasons matter —
 *
 *   - It is one object with several faces, so the faces should behave alike.
 *     A ring you step through and a ring you drag numbers into are two
 *     different controls wearing one shape.
 *   - The modal calculator (WorkUnitCalculator) already does editing, with
 *     five sliders and the same maths, and it is good. Rebuilding it inline
 *     with a picture on top would be a duplicate, and duplicates drift.
 *
 * So this answers "what does a month look like?" at a glance and the launcher
 * beneath it still answers "what would MY month cost?". Whether the modal is
 * later redundant is a decision to make with this on the page, not before.
 *
 * `monthArcs()` already accepts arbitrary counts, so making the ring editable
 * later is a change to this file only.
 */

import React from 'react';
import styled from 'styled-components';
import { media } from '../../styles/breakpoints';
import { WU_LABEL } from '../../config/workUnits';
import {
  MONTH_TOTAL_WU,
  monthArcs,
  typicalCounts,
  type MonthArc,
} from './zeroMonth';

const R = 104;
const RING_W = 26;
const CX = 130;
const CY = 130;

/**
 * The five arcs need to be told apart at a glance without leaving the palette,
 * which has exactly one accent. So they step through the gold's own opacity
 * rather than inventing colours — and the SELECTED arc is the only one at full
 * strength, which is what carries the reading. (An invented green cost this
 * work a design-token gate failure once already.)
 */
const ARC_TONES = [0.9, 0.72, 0.54, 0.38, 0.24];

const Frame = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 1.75rem;
  align-items: center;
  justify-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1.1rem;
  }
`;

const Dial = styled.svg`
  /* Below the tablet breakpoint the BARS below are the control instead.
     display:none (not visibility) so the hidden one leaves the accessibility
     tree and the tab order — there is never a second slider for one month.
     No backticks in this comment: it is inside a template literal. */
  display: none;
  ${media.tablet} {
    display: block;
  }
  width: 260px;
  height: 260px;
  border-radius: 50%;

  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 6px;
  }
`;

const Bars = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 30rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;

  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 4px;
  }

  ${media.tablet} {
    display: none;
  }
`;

const Bar = styled.li<{ $on: boolean }>`
  cursor: pointer;
  padding: 0.35rem 0;
`;

const BarHead = styled.div<{ $on: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.84rem;
  line-height: 1.45;
  color: ${(p) => (p.$on ? 'var(--brand-text)' : 'var(--brand-text-secondary)')};
  font-weight: ${(p) => (p.$on ? 600 : 400)};
`;

const BarTrack = styled.div`
  margin-top: 0.3rem;
  height: 8px;
  border-radius: 999px;
  background: var(--brand-surface);
  border: 1px solid var(--brand-line);
  overflow: hidden;
`;

const BarFill = styled.div<{ $share: number; $tone: number }>`
  height: 100%;
  width: ${(p) => Math.min(100, p.$share * 100)}%;
  background: var(--brand-gold);
  opacity: ${(p) => p.$tone};
`;

const Readout = styled.div`
  max-width: 26rem;
`;

const Slice = styled.div`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.15rem;
  color: var(--brand-text);
  line-height: 1.3;
`;

const Detail = styled.div`
  margin-top: 0.4rem;
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--brand-text-secondary);
`;

const Whole = styled.div`
  margin-top: 0.9rem;
  padding-top: 0.9rem;
  border-top: 1px solid var(--brand-line);
  font-size: 0.84rem;
  line-height: 1.6;
  color: var(--brand-text-secondary);
`;

const Hint = styled.div`
  margin-top: 0.6rem;
  font-size: 0.78rem;
  color: var(--brand-text-placeholder);
`;

const Live = styled.span`
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0);
  white-space: nowrap; border: 0;
`;

function polar(cx: number, cy: number, r: number, deg: number) {
  const a = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

/**
 * One ring segment as a stroked arc. Stroked rather than filled because a
 * stroke gives a constant band width for free; a filled donut wedge needs two
 * radii and four points to say the same thing.
 */
function arcPath(startDeg: number, endDeg: number): string {
  // A segment that would close the whole circle cannot be drawn as one arc —
  // start and end land on the same point and the renderer draws nothing.
  const sweep = Math.min(359.99, Math.max(0, endDeg - startDeg));
  const a = polar(CX, CY, R, startDeg);
  const b = polar(CX, CY, R, startDeg + sweep);
  return `M ${a.x} ${a.y} A ${R} ${R} 0 ${sweep > 180 ? 1 : 0} 1 ${b.x} ${b.y}`;
}

export interface ZeroMonthProps {
  /** Auto-advance through the slices until the visitor takes over. */
  autoPlay?: boolean;
}

export const ZeroMonth: React.FC<ZeroMonthProps> = ({ autoPlay = true }) => {
  const arcs = React.useMemo<MonthArc[]>(() => monthArcs(typicalCounts()), []);
  const [index, setIndex] = React.useState(0);
  const [touched, setTouched] = React.useState(false);

  const reduceMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const playing = autoPlay && !touched && !reduceMotion;

  React.useEffect(() => {
    if (!playing) return undefined;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % arcs.length), 3200);
    return () => window.clearInterval(id);
  }, [playing, arcs.length]);

  const take = (next: number) => {
    setTouched(true);
    setIndex(((next % arcs.length) + arcs.length) % arcs.length);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const k = e.key;
    if (k === 'ArrowRight' || k === 'ArrowUp') { e.preventDefault(); take(index + 1); }
    else if (k === 'ArrowLeft' || k === 'ArrowDown') { e.preventDefault(); take(index - 1); }
    else if (k === 'Home') { e.preventDefault(); take(0); }
    else if (k === 'End') { e.preventDefault(); take(arcs.length - 1); }
  };

  const current = arcs[Math.min(index, arcs.length - 1)];
  const pct = Math.round(current.share * 100);

  /**
   * ONE SLIDER CONTRACT, spread onto whichever presentation CSS is showing —
   * the same arrangement Zero.tsx and DayStrip use, for the same reason: two
   * copies drift, and the copy that drifts is the phone.
   */
  const sliderProps = {
    role: 'slider' as const,
    tabIndex: 0,
    'aria-label': `what one Prest0n's month of ${MONTH_TOTAL_WU.toLocaleString('en-US')} ${WU_LABEL} goes on — use the arrow keys`,
    'aria-valuemin': 0,
    'aria-valuemax': arcs.length - 1,
    'aria-valuenow': index,
    'aria-valuetext': `${current.item} — ${current.wu} ${WU_LABEL}, ${pct}% of the month`,
    onKeyDown,
  };

  return (
    <Frame>
      <Dial viewBox="0 0 260 260" {...sliderProps} data-month-ring="">
        {/* the unspent ring beneath — the month, before anything is on it */}
        <circle
          cx={CX} cy={CY} r={R}
          fill="none"
          stroke="var(--brand-line)"
          strokeWidth={RING_W}
          opacity="0.5"
        />
        {arcs.map((a, i) => (
          <path
            key={a.key}
            d={arcPath(a.startAngle, a.endAngle)}
            fill="none"
            stroke="var(--brand-gold)"
            strokeWidth={i === index ? RING_W + 6 : RING_W}
            opacity={i === index ? 1 : ARC_TONES[i % ARC_TONES.length]}
            style={{ cursor: 'pointer' }}
            onClick={() => take(i)}
          />
        ))}

        {/* the total, in the middle of its own circle */}
        <text
          x={CX} y={CY - 4}
          textAnchor="middle"
          fontFamily="'Space Grotesk', sans-serif"
          fontSize="26"
          fill="var(--brand-text)"
        >
          {MONTH_TOTAL_WU.toLocaleString('en-US')}
        </text>
        <text
          x={CX} y={CY + 18}
          textAnchor="middle"
          fontFamily="'Inter', sans-serif"
          fontSize="12"
          fill="var(--brand-text-secondary)"
        >
          {WU_LABEL} a month
        </text>
      </Dial>

      {/* The same month as stacked bars, for phones. Only one of the two is
          ever present to a user — the swap is display:none on each. */}
      <Bars {...sliderProps} data-month-bars="">
        {arcs.map((a, i) => (
          <Bar key={a.key} $on={i === index} onClick={() => take(i)} data-month-slice={i === index ? 'current' : ''}>
            <BarHead $on={i === index}>
              <span>{a.item}</span>
              <span>{a.wu} {WU_LABEL}</span>
            </BarHead>
            <BarTrack>
              <BarFill $share={a.share} $tone={i === index ? 1 : ARC_TONES[i % ARC_TONES.length]} />
            </BarTrack>
          </Bar>
        ))}
      </Bars>

      <Readout>
        <Slice>{a11yCount(current)} {current.item}</Slice>
        <Detail>
          {current.wuEach} {WU_LABEL} each — the published rate. that is{' '}
          <strong>{current.wu} {WU_LABEL}</strong>, {pct}% of the month.
        </Detail>
        <Whole>
          the whole circle is {MONTH_TOTAL_WU.toLocaleString('en-US')} {WU_LABEL} — what one
          Prest0n brings every month. these five together are exactly that, which is why the
          mark and the price are the same picture.
        </Whole>
        <Hint>tap a slice, or use the arrow keys</Hint>
        <Live aria-live="polite">
          {`${current.item}. ${current.wu} ${WU_LABEL}, ${pct} percent of the month.`}
        </Live>
      </Readout>
    </Frame>
  );
};

/** The count, grouped — "1,200" reads better than "1200" beside a noun. */
function a11yCount(arc: MonthArc): string {
  return arc.count.toLocaleString('en-US');
}

export default ZeroMonth;
